import { useState, useEffect } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Input from "../../../UI/Input/Input";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Button from "../../../UI/Button/Button";
import server from "../../../../apis/server";

import classes from "./Create.module.scss";

const VolunteerCreate = ({
  volunteerPositionFromList,
  clearSelectedPosition,
  positions,
  setPositions,
  setSelectedRows,
}) => {
  const { authUser } = useAuth();
  const navigate = useNavigate();

  const [descriptionValue, setDescriptionValue] = useState("");
  const [saveEnabled, setSaveEnabled] = useState(null);
  const [publish, setPublish] = useState(false);
  const [publishEnabled, setPublishEnabled] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [volunteerForm, setVolunteerForm] = useState({
    position: {
      elementType: "input",
      elementConfig: { type: "text", placeholder: "Position" },
      value: "",
      validation: { required: true },
      width: "70%",
    },
    image: {
      elementType: "image",
      elementConfig: { placeholder: "Image", url: "", file: "" },
      value: "",
      validation: { required: false },
    },
    frequency: {
      elementType: "select",
      elementConfig: {
        options: [
          { value: "one-time", displayValue: "One-Time" },
          { value: "ongoing", displayValue: "Ongoing" },
        ],
      },
      value: "one-time",
      validation: { required: true },
    },
    /* 1. NEW STATE ADDITION: Tracks priority flag inputs inside your dynamic UI */
    isFeatured: {
      elementType: "select",
      elementConfig: {
        options: [
          { value: "false", displayValue: "Standard Position" },
          { value: "true", displayValue: "Featured (Top Billing)" },
        ],
      },
      value: "false",
      validation: { required: true },
    },
    description: {
      elementType: "richtext",
    },
    primaryContact: {
      elementType: "input",
      elementConfig: { type: "text", placeholder: "Primary contact" },
      value: "",
      validation: { required: true },
      width: "70%",
    },
    primaryContactEmail: {
      elementType: "input",
      elementConfig: {
        elementType: "email",
        placeholder: "Primary contact email",
      },
      value: "",
      validation: { required: true },
      width: "70%",
    },
    sponsoringMinistry: {
      elementType: "input",
      elementConfig: { type: "text", placeholder: "Sponsoring ministry" },
      value: "",
      validation: { required: true },
      width: "70%",
    },
  });

  /* ========================================================
     NEW EFFECT: Syncs incoming data into the nested state object
     ======================================================== */
  useEffect(() => {
    const activePosition = volunteerPositionFromList || selectedPosition;

    if (activePosition) {
      setSelectedPosition(activePosition);
      setPublish(!!activePosition.published);
      setPublishEnabled(true);
      setDescriptionValue(activePosition.description || "");

      const imageUrl =
        activePosition.Image && activePosition.Image.url
          ? activePosition.Image.url
          : "";

      setVolunteerForm((prevForm) => ({
        ...prevForm,
        position: {
          ...prevForm.position,
          value: activePosition.position || "",
        },
        image: {
          ...prevForm.image,
          elementConfig: {
            ...prevForm.image.elementConfig,
            url: imageUrl,
            file: "",
          },
        },
        frequency: {
          ...prevForm.frequency,
          value: activePosition.frequency || "one-time",
        },
        /* 2. UPDATE DATA SYNC HOOK: Correctly translates database boolean flags back into strings for form selectors */
        isFeatured: {
          ...prevForm.isFeatured,
          value:
            activePosition.isFeatured !== undefined
              ? String(activePosition.isFeatured)
              : "false",
        },
        primaryContact: {
          ...prevForm.primaryContact,
          value: activePosition.primaryContact || "",
        },
        primaryContactEmail: {
          ...prevForm.primaryContactEmail,
          value: activePosition.primaryContactEmail || "",
        },
        sponsoringMinistry: {
          ...prevForm.sponsoringMinistry,
          value: activePosition.sponsoringMinistry || "",
        },
      }));
    }
  }, [volunteerPositionFromList, selectedPosition]);

  useEffect(() => {
    const activePosition = volunteerPositionFromList || selectedPosition;
    if (activePosition && descriptionValue !== activePosition.description) {
      setSaveEnabled(true);
    }
  }, [descriptionValue, volunteerPositionFromList, selectedPosition]);

  const inputChangedHandler = (e, inputIdentifier) => {
    setSaveEnabled(true);
    const updatedVolunteerForm = { ...volunteerForm };
    const updatedFormElement = {
      ...updatedVolunteerForm[inputIdentifier],
      elementConfig: { ...updatedVolunteerForm[inputIdentifier].elementConfig },
    };

    if (updatedFormElement.elementType === "image") {
      if (e.target.files && e.target.files[0]) {
        updatedFormElement.elementConfig.url = URL.createObjectURL(
          e.target.files[0],
        );
        updatedFormElement.elementConfig.file = e.target.files[0];
      }
    } else if (updatedFormElement.elementType === "richtext") {
      updatedFormElement.value = e;
    } else {
      updatedFormElement.value = e.target.value;
    }

    updatedFormElement.touched = true;
    updatedVolunteerForm[inputIdentifier] = updatedFormElement;
    setVolunteerForm(updatedVolunteerForm);
  };

  const backClickHandler = () => {
    setSelectedRows(null);
    clearSelectedPosition(null);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    await server.put("/volunteer/publish", {
      positionId: selectedPosition.id,
      published: !publish,
      orgId: authUser.orgId,
    });

    if (positions) {
      const revisedPositions = positions.map((position) => {
        return position.id === selectedPosition.id
          ? { ...selectedPosition, published: !publish }
          : position;
      });
      setPositions(revisedPositions);
    }

    setPublish(!publish);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let volunteerFormValues = new FormData();

    volunteerFormValues.append("position", volunteerForm.position.value);
    volunteerFormValues.append("image", volunteerForm.image.elementConfig.file);
    volunteerFormValues.append("frequency", volunteerForm.frequency.value);

    /* 3. APPEND PAYLOAD TO FORMDATA: Transports your configuration choice securely to your Node API endpoints */
    volunteerFormValues.append("isFeatured", volunteerForm.isFeatured.value);

    volunteerFormValues.append("description", descriptionValue);
    volunteerFormValues.append(
      "primaryContact",
      volunteerForm.primaryContact.value,
    );
    volunteerFormValues.append(
      "primaryContactEmail",
      volunteerForm.primaryContactEmail.value,
    );
    volunteerFormValues.append(
      "sponsoringMinistry",
      volunteerForm.sponsoringMinistry.value,
    );
    volunteerFormValues.append("userId", authUser.id);
    volunteerFormValues.append("orgId", authUser.orgId);
    volunteerFormValues.append("published", publish);

    let volunteerResponse;

    if (selectedPosition) {
      volunteerFormValues.append("id", selectedPosition.id);
      volunteerResponse = await server.put("/volunteer", volunteerFormValues);
    } else {
      volunteerResponse = await server.post("/volunteer", volunteerFormValues, {
        headers: { "content-type": "multipart/form-data" },
      });
    }

    if (volunteerResponse.status === 200) {
      setSaveEnabled(false);
      setPublishEnabled(true);
      setSelectedPosition(volunteerResponse.data);

      if (positions) {
        const isExisting = positions.some(
          (p) => p.id === volunteerResponse.data.id,
        );
        const revisedPositions = isExisting
          ? positions.map((p) =>
              p.id === volunteerResponse.data.id
                ? { ...volunteerResponse.data }
                : p,
            )
          : [...positions, volunteerResponse.data];
        setPositions(revisedPositions);
      }
    }
  };

  /* 1. Build the base form elements array from state */
  let formElementsArray = [];
  for (let key in volunteerForm) {
    formElementsArray.push({
      id: key,
      config: volunteerForm[key],
    });
  }

  /* 2. THE SECURITY GATEKEEPER: Hides the field if the logged-in user is not an admin */
  if (authUser && authUser.role !== "admin") {
    formElementsArray = formElementsArray.filter(
      (formElement) => formElement.id !== "isFeatured",
    );
  }

  return (
    <div className={classes.PositionSubmission}>
      {selectedPosition ? (
        <Button
          clicked={positions ? backClickHandler : () => navigate("/volunteer")}
        >
          &larr; Back
        </Button>
      ) : null}
      <div className={classes.PositionSubmission__TopInfo}>
        <h2>
          {selectedPosition ? `Edit a` : `Create a new`} Volunteer position
        </h2>
        <div className={classes.PositionSubmission__TopInfo__Buttons}>
          <Button disabled={!publishEnabled} clicked={handlePublish}>
            {publish ? `Unpublish` : `Publish`}
          </Button>
          <Button disabled={!saveEnabled} clicked={handleSubmit}>
            Save
          </Button>
        </div>
        <br />
        <br />
      </div>

      <form encType="multipart/form-data">
        {formElementsArray.map((formElement) => {
          if (formElement.config.elementType === "richtext") {
            return (
              <ReactQuill
                theme="snow"
                value={descriptionValue}
                onChange={setDescriptionValue}
                key={formElement.id}
                className={classes.Quill}
              />
            );
          } else {
            return (
              <Input
                key={formElement.id}
                elementType={formElement.config.elementType}
                elementConfig={formElement.config.elementConfig}
                value={formElement.config.value}
                changed={(e) => inputChangedHandler(e, formElement.id)}
                required={formElement.config.validation?.required}
                width={formElement.config.width}
              />
            );
          }
        })}
      </form>
    </div>
  );
};

export default VolunteerCreate;
